import { createContext, useContext } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import type { Components, ExtraProps } from 'react-markdown'
import type { ThreadMarkdownRenderers } from '@/types/components/thread-markdown-renderers'

export const ThreadMarkdownRendererContext =
  createContext<ThreadMarkdownRenderers | null>(null)

function useRenderers() {
  const renderers = useContext(ThreadMarkdownRendererContext)
  if (!renderers)
    throw new Error('Thread Markdown renderers require their provider')
  return renderers
}

function MarkdownPre(props: ComponentPropsWithoutRef<'pre'> & ExtraProps) {
  return useRenderers().renderPre(props)
}
function MarkdownLink(props: ComponentPropsWithoutRef<'a'> & ExtraProps) {
  return useRenderers().renderLink(props)
}
function MarkdownImage(props: ComponentPropsWithoutRef<'img'> & ExtraProps) {
  return useRenderers().renderImage(props)
}

// In-answer headings must say `font-sans` out loud: globals.css wires every
// h1-h3 to `--font-display` (a serif) so the heading font picker can retheme
// page titles. That display face is for titles, not for headings sitting
// inside a body of prose, so each renderer below opts back out explicitly.
//
// Weight carries the hierarchy first: `font-semibold` makes a heading read as
// a heading at a glance, even mid-scroll through a generated answer that
// stacks several of them. Size and spacing still separate the levels from
// each other, and `text-foreground` keeps headings darker than the prose
// around them.
const HEADING_BASE = 'font-sans font-semibold text-foreground first:mt-0'

// A fenced block carries `language-*` from remark; inline code carries nothing.
// Only the inline case gets the pill — the block case is styled by `pre`, which
// also neutralises any pill that a language-less fence would otherwise pick up.
function codeClassName(className?: string): string {
  if (className) {
    return className
  }
  return 'rounded bg-muted px-1 py-0.5 text-[0.9em]'
}

/** Stable node types keep images, lightboxes and rich blocks mounted across chat updates. */
export const THREAD_MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1
      className={`mb-2 mt-10 text-lg leading-tight ${HEADING_BASE}`}
      data-testid="thread-answer-heading-1"
    >
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2
      className={`mb-2 mt-10 text-base leading-snug ${HEADING_BASE}`}
      data-testid="thread-answer-heading-2"
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      className={`mb-1 mt-9 text-base leading-snug ${HEADING_BASE}`}
      data-testid="thread-answer-heading-3"
    >
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4
      className={`mb-1 mt-6 text-base leading-snug ${HEADING_BASE}`}
      data-testid="thread-answer-heading-4"
    >
      {children}
    </h4>
  ),
  p: ({ children }) => <p className="my-4 first:mt-0 last:mb-0">{children}</p>,
  strong: ({ children }) => (
    // Matches the heading weight (`font-semibold`) so a bulleted
    // `**label:**` lead-in reads at the same weight as a heading,
    // not a separate, heavier emphasis of its own.
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  ul: ({ children }) => (
    <ul className="my-4 ml-5 list-disc space-y-1 first:mt-0 last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 ml-5 list-decimal space-y-1 first:mt-0 last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="my-1 leading-[1.7]">{children}</li>,
  hr: () => (
    <hr
      className="my-10 border-t border-border/40"
      data-testid="thread-answer-rule"
    />
  ),
  blockquote: ({ children }) => (
    <blockquote
      className="my-6 border-l-2 border-border/60 pl-4 italic text-muted-foreground"
      data-testid="thread-answer-blockquote"
    >
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div
      className="my-6 overflow-x-auto"
      data-testid="thread-answer-table-scroll"
    >
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-border">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-border/30 last:border-0">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-medium">{children}</th>
  ),
  td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
  code: ({ className, children }) => (
    <code className={codeClassName(className)}>{children}</code>
  ),
  pre: MarkdownPre,
  a: MarkdownLink,
  img: MarkdownImage,
}
